import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, CreditCard, Mail, FileText, Check, X } from "lucide-react";
import { HeroFade, Reveal } from "../components/Motion";
import { MarqueeStrip } from "../components/MarqueeStrip";
import { STRIPE_URL, EMAIL } from "../constants";

const PRODUCT_IMG =
  "https://images.unsplash.com/photo-1489875347897-49f64b51c1f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxsYXB0b3AlMjBzY3JlZW4lMjBjb2RlJTIwZGFya3xlbnwwfHx8fDE3ODc5MzE5ODJ8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  {
    num: "01",
    icon: CreditCard,
    title: "Pay $99",
    body: "Card payment via Stripe on this page. One flat fee, no subscription, nothing recurring.",
  },
  {
    num: "02",
    icon: Mail,
    title: "Send Your Address",
    body: `After payment, email ${EMAIL} with your website address — if we do not already have it.`,
  },
  {
    num: "03",
    icon: FileText,
    title: "Get The Report",
    body: "We examine your public pages and send the written report within 3 business days.",
  },
];

const YOU_GET = [
  {
    title: "A written English report",
    body: "Covering your public small-business website — about 8–15 unique pages, read by a human.",
    large: true,
  },
  { title: "Problems found", body: "Every accessibility issue we identify, listed plainly." },
  { title: "What to change", body: "Concrete fixes you or your web person can execute." },
  { title: "3 business days", body: "Delivery after payment. You keep the report forever." },
];

const YOU_DONT_GET = [
  "We do not log in to your site or accounts",
  "We do not change the live site",
  "We do not certify compliance with any accessibility law",
  "We do not sue anyone, or negotiate with lawyers",
  "We do not promise the site is lawsuit-proof",
];

export default function Report() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <main data-testid="report-page" className="pb-28">
      <section className="relative overflow-hidden px-6 pt-36 md:px-10 md:pt-44">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl pb-20">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
            <div>
              <HeroFade delay={0.1}>
                <p className="mb-6 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                  Product — Website Issue Report
                </p>
              </HeroFade>
              <h1 data-testid="report-heading" className="font-serif text-4xl font-medium leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                <HeroFade delay={0.2}>The $99 Website</HeroFade>
                <HeroFade delay={0.35}>
                  Issue <em className="text-blue-600">Report.</em>
                </HeroFade>
              </h1>
              <HeroFade delay={0.5}>
                <p data-testid="report-lead" className="mt-10 max-w-xl text-base leading-relaxed text-slate-700 md:text-lg">
                  In the US, private businesses keep getting sued over websites that are
                  hard to use for people who are blind or who only use a keyboard. There
                  is no federal "fix by this date" rule for a restaurant, clinic, or shop.
                  The cases still happen. In 2025 there were more than 3,000 federal
                  website cases — 2026 is on pace to be higher. New York, Florida,
                  California, and Illinois see a lot of them.
                </p>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-700 md:text-lg">
                  What usually happens: a lawyer sends a demand letter. Many small owners
                  pay $5,000 to $20,000 to settle, then still have to fix the site. If it
                  becomes a filed case, costs go up. Paying the letter does not stop the
                  next lawyer. A cheap accessibility widget on the site does not stop
                  these cases.
                </p>
                <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-500">
                  We do not sue anyone. We look at your public pages and send a written
                  report of the problems and what to change. You or your web person do
                  the work.
                </p>
              </HeroFade>
            </div>
            <HeroFade delay={0.65}>
              <div className="rounded-2xl bg-white p-8 shadow-lift md:p-10">
                <div className="mb-6 flex items-end justify-between">
                  <span className="font-serif text-6xl font-medium tracking-tight text-slate-900 md:text-7xl">
                    $99
                  </span>
                  <span className="rounded-full bg-blue-50 px-4 py-1.5 text-xs font-semibold text-blue-700">
                    Flat / Once
                  </span>
                </div>
                <ul className="mb-8 space-y-2.5 text-sm font-medium text-slate-600">
                  <li data-testid="price-fact-card" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> Card — via Stripe
                  </li>
                  <li data-testid="price-fact-delivery" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> 3 business days
                  </li>
                  <li data-testid="price-fact-list" className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 text-blue-600" /> Full list after payment
                  </li>
                </ul>
                <a
                  href={STRIPE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pay-stripe-button"
                  className="group flex items-center justify-center gap-2.5 rounded-full bg-blue-600 px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
                >
                  Pay $99 — Website Issue Report
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <p className="mt-4 text-xs leading-relaxed text-slate-400">
                  Payment processed by Stripe. After you pay, email us the site address if we do not already have it.
                </p>
              </div>
            </HeroFade>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
        <Reveal>
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
            Process
          </p>
          <h2 data-testid="how-it-works-heading" className="mb-16 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-slate-900 md:text-5xl">
            How it works.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.1}>
              <article
                data-testid={`step-${s.num}`}
                className="group flex h-full flex-col gap-6 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift md:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-4xl font-medium italic text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                    {s.num}
                  </span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 transition-colors duration-300 group-hover:bg-blue-100">
                    <s.icon className="h-5 w-5 text-blue-600" />
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">{s.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section ref={imgRef} className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="relative h-[50vh] overflow-hidden rounded-2xl shadow-lift md:h-[65vh]">
          <motion.img
            src={PRODUCT_IMG}
            alt="Laptop screen showing code in a dark room"
            data-testid="report-parallax-image"
            style={{ y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 rounded-full bg-white/90 px-5 py-2.5 text-xs font-semibold text-slate-700 backdrop-blur-sm">
            The audit, in progress — Fig. 02
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
          <div>
            <Reveal>
              <h2 data-testid="you-get-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-900 md:text-5xl">
                What you <em className="text-blue-600">get.</em>
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {YOU_GET.map((item, i) => (
                <Reveal
                  key={item.title}
                  delay={i * 0.08}
                  className={item.large ? "sm:col-span-2" : ""}
                >
                  <div
                    data-testid={`you-get-item-${i}`}
                    className="flex h-full flex-col gap-4 rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                      <Check className="h-4 w-4 text-blue-600" />
                    </span>
                    <h3 className={`font-serif font-medium tracking-tight text-slate-900 ${item.large ? "text-2xl md:text-3xl" : "text-xl"}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-600">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <h2 data-testid="you-dont-get-heading" className="mb-12 font-serif text-3xl font-medium tracking-tight text-slate-400 md:text-5xl">
                What you don't get.
              </h2>
            </Reveal>
            <ul className="flex flex-col gap-4">
              {YOU_DONT_GET.map((item, i) => (
                <Reveal key={item} delay={i * 0.08}>
                  <li
                    data-testid={`you-dont-get-item-${i}`}
                    className="flex items-center gap-4 rounded-2xl bg-slate-50 px-6 py-5 transition-colors duration-300 hover:bg-slate-100"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-soft">
                      <X className="h-4 w-4 text-slate-400" />
                    </span>
                    <span className="text-sm leading-relaxed text-slate-600 md:text-base">
                      {item}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.4}>
              <p className="mt-8 text-sm font-medium text-slate-500">
                Straight terms. No fine print. That is the whole product.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200/70 bg-cream/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4 md:px-10">
          <div className="hidden items-baseline gap-3 md:flex">
            <span className="font-serif text-2xl font-medium text-slate-900">$99</span>
            <span className="text-sm text-slate-500">
              Report in 3 business days
            </span>
          </div>
          <a
            href={STRIPE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sticky-pay-button"
            className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md md:w-auto"
          >
            Pay $99 via Stripe
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </main>
  );
}
